import { useState, useMemo } from 'react';
import { useSchedule } from '../context/ScheduleContext';
import { RiskBadge } from './RiskBadge';
import { ReminderStatusIcon } from './ReminderStatusIcon';
import type { RiskLevel } from './riskTypes';

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

export function RiskSummaryPanel() {
  const { noShowRiskHandle, events } = useSchedule();
  const [expanded, setExpanded] = useState(false);

  const { riskScoresMap, getReminderState, requestRiskScores, machineState } = noShowRiskHandle;

  // Build id→plain-text-title lookup, stripping any HTML.
  const titleMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of events) {
      const raw = e.title ?? '';
      m.set(e.id, HTML_TAG_RE.test(raw) ? stripHtml(raw) : raw);
    }
    return m;
  }, [events]);

  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, moderate: 0, low: 0 };
    for (const [, score] of riskScoresMap) {
      c[score.riskLevel]++;
    }
    return c;
  }, [riskScoresMap]);

  // Expanded list shows critical + high + moderate, sorted by score descending.
  const riskEntries = useMemo(() => (
    [...riskScoresMap.values()]
      .filter((s) => s.riskLevel !== 'low')
      .sort((a, b) => b.riskScore - a.riskScore)
  ), [riskScoresMap]);

  const hasRisk = counts.critical > 0 || counts.high > 0 || counts.moderate > 0;
  if (!hasRisk && machineState !== 'RISK_ACTIVE') return null;

  return (
    <div
      style={{
        background: '#fafafa',
        borderBottom: '1px solid #e2e8f0',
        fontSize: 11,
        userSelect: 'none',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 12px',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span style={{ color: '#64748b', fontSize: 12 }}>📊</span>
        <span style={{ fontWeight: 600, color: '#1e293b' }}>No-show risk</span>
        {counts.critical > 0 && <Chip count={counts.critical} level="critical" />}
        {counts.high > 0 && <Chip count={counts.high} level="high" />}
        {counts.moderate > 0 && <Chip count={counts.moderate} level="moderate" />}
        {machineState === 'AWAITING_SCORES' && (
          <span style={{ color: '#94a3b8', fontSize: 10 }}>Loading…</span>
        )}
        <span style={{ flex: 1 }} />
        <button
          onClick={(e) => { e.stopPropagation(); requestRiskScores(); }}
          title="Refresh risk scores"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#94a3b8',
            fontSize: 11,
            padding: '0 2px',
          }}
        >
          ↺
        </button>
        <span style={{ color: '#94a3b8', fontSize: 10 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded list */}
      {expanded && riskEntries.length > 0 && (
        <div style={{ borderTop: '1px solid #e2e8f0', maxHeight: 200, overflowY: 'auto' }}>
          {riskEntries.map((score) => {
            const reminderState = getReminderState(score.appointmentId);
            const title = titleMap.get(score.appointmentId) ?? score.appointmentId;
            return (
              <div
                key={score.appointmentId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 12px',
                  borderBottom: '1px solid #f1f5f9',
                  fontSize: 11,
                }}
              >
                <RiskBadge
                  riskLevel={score.riskLevel}
                  riskFactors={score.riskFactors}
                  suppressReminders={score.suppressReminders ?? false}
                />
                <span
                  title={title}
                  style={{ flex: 1, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {title}
                </span>
                <span style={{ color: '#64748b', fontSize: 10 }}>{score.riskScore}</span>
                {reminderState && (
                  <ReminderStatusIcon
                    appointmentStatus={reminderState.status}
                    lastReminderOutcome={reminderState.lastReminderOutcome}
                    firedCount={reminderState.firedReminders.length}
                    outreachFlagged={reminderState.outreachFlagged}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {expanded && riskEntries.length === 0 && (
        <div style={{ padding: '8px 12px', color: '#94a3b8', borderTop: '1px solid #e2e8f0' }}>
          No elevated risk appointments in view.
        </div>
      )}
    </div>
  );
}

function Chip({ count, level }: { count: number; level: RiskLevel }) {
  const colors: Record<RiskLevel, { bg: string; color: string }> = {
    critical: { bg: '#ef4444', color: '#fff' },
    high:     { bg: '#fef2f2', color: '#ef4444' },
    moderate: { bg: '#fffbeb', color: '#b45309' },
    low:      { bg: '#f0fdf4', color: '#15803d' },
  };
  const c = colors[level];
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      padding: '1px 6px',
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700,
      background: c.bg,
      color: c.color,
    }}>
      {count} {level}
    </span>
  );
}
